import { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Fade,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import AssistantAvatar from "src/views/assistant/components/ui/AssistantAvatar";
import {
  useActivePersonaIdentity,
  useChatStore,
} from "src/views/assistant/stores/ChatStore";
import AssistantTextContainer from "../AssistantMessage/AssistantTextContainer";
import { formatElapsed } from "src/reusable/utils/formatElapsed";

const PHRASES = [
  "Thinking through your request",
  "Analyzing the conversation",
  "Formulating a response",
  "Reviewing available information",
  "Putting together an answer",
];

const PHRASE_INTERVAL = 4500;

// How long the "Finding the best workflow" routing hint may show before we
// fall back to the rotating "thinking" phrases. Classification finishes during
// POST /chat/start (tens of ms), and a matched persona's routing event arrives
// within a beat — but a request that resolves to no persona (general chat)
// never sets `routing`, so without this bound the hint would freeze on screen
// for the entire (remote-model) time-to-first-token. Keep it to one beat.
const ROUTING_HINT_MS = 4000;

/** Animated orbital ring -- lightweight pure-CSS atom-style spinner */
const OrbitalSpinner = () => {
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  const primaryLight = theme.palette.primary.light;

  return (
    <Box
      sx={{
        position: "relative",
        width: 28,
        height: 28,
        flexShrink: 0,
      }}
    >
      {/* Core dot */}
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          width: 6,
          height: 6,
          borderRadius: "50%",
          backgroundColor: primary,
          transform: "translate(-50%, -50%)",
          animation: "corePulse 2s ease-in-out infinite",
          "@keyframes corePulse": {
            "0%, 100%": {
              opacity: 1,
              transform: "translate(-50%, -50%) scale(1)",
            },
            "50%": {
              opacity: 0.6,
              transform: "translate(-50%, -50%) scale(0.7)",
            },
          },
        }}
      />
      {/* Orbit ring */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          borderRadius: "50%",
          border: `1.5px solid transparent`,
          borderTopColor: primary,
          borderRightColor: primaryLight,
          animation: "orbit 1.8s linear infinite",
          "@keyframes orbit": {
            "0%": { transform: "rotate(0deg)" },
            "100%": { transform: "rotate(360deg)" },
          },
        }}
      />
      {/* Second orbit — tilted */}
      <Box
        sx={{
          position: "absolute",
          inset: 2,
          borderRadius: "50%",
          border: `1px solid transparent`,
          borderBottomColor: primaryLight,
          opacity: 0.5,
          animation: "orbit 2.6s linear infinite reverse",
        }}
      />
    </Box>
  );
};

/** Elapsed time display — shows seconds since indicator appeared */
const ElapsedTimer = () => {
  const [seconds, setSeconds] = useState(0);
  const startRef = useRef(Date.now());

  useEffect(() => {
    const interval = setInterval(() => {
      setSeconds(Math.floor((Date.now() - startRef.current) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  if (seconds < 2) return null;

  return (
    <Typography
      variant="body2"
      sx={{
        color: "text.disabled",
        fontVariantNumeric: "tabular-nums",
        ml: 0.5,
      }}
    >
      {formatElapsed(seconds)}
    </Typography>
  );
};

type AtomAssistTypingIndicatorProps = {
  action?: "typing" | "thinking";
  /** When false, the elapsed-seconds counter is hidden. Use this to suppress
   *  the timer during active agent work (tool loops) where it adds no value. */
  showTimer?: boolean;
  /** When true, a sub-agent (or other background task) is actively running —
   *  show an explicit "working in the background, standby" status instead of
   *  the generic rotating phrases. */
  backgroundWork?: boolean;
};

const AtomAssistTypingIndicator = ({
  action,
  showTimer = true,
  backgroundWork = false,
}: AtomAssistTypingIndicatorProps) => {
  const { productConfig, routing } = useChatStore();
  const identity = useActivePersonaIdentity();
  const theme = useTheme();
  const lessThanMdBreakpoint = useMediaQuery(theme.breakpoints.down("md"));

  const isAutoStart =
    window.location.pathname.includes("/chat/") &&
    !action &&
    document.querySelector('[data-testid="chat-messages"]')?.children.length ===
      0;

  const isThinking = !action && !isAutoStart;

  // Crossfade phrase rotation
  const [phraseIndex, setPhraseIndex] = useState(0);
  const [visible, setVisible] = useState(true);

  const rotate = useCallback(() => {
    setVisible(false);
    setTimeout(() => {
      setPhraseIndex((i) => (i + 1) % PHRASES.length);
      setVisible(true);
    }, 300);
  }, []);

  useEffect(() => {
    if (!isThinking) return;
    const interval = setInterval(rotate, PHRASE_INTERVAL);
    return () => clearInterval(interval);
  }, [isThinking, rotate]);

  // The routing hint is only honest for a brief beat after the indicator
  // appears. After that, classification is certainly done (it ran in
  // /chat/start) and the model is generating — so fall back to the rotating
  // thinking phrases instead of freezing on "Finding the best workflow".
  const [routingHintActive, setRoutingHintActive] = useState(true);
  useEffect(() => {
    const t = setTimeout(() => setRoutingHintActive(false), ROUTING_HINT_MS);
    return () => clearTimeout(t);
  }, []);

  // Show routing status during initial classification delay (first message only).
  // Only for Atom Assist — other products don't use intelligent routing, so
  // routing stays null permanently and would incorrectly show the routing message.
  // Skip when user explicitly selected a persona (clicked a workflow) — no
  // classification is running, so "finding the best workflow" is misleading.
  const isRoutingProduct = productConfig.productKey === "atom-assist";
  const hasExplicitPersona = identity.source === "manual";
  const isRouting =
    isRoutingProduct &&
    routing === null &&
    routingHintActive &&
    !action &&
    !isAutoStart &&
    !hasExplicitPersona;

  const statusText = backgroundWork
    ? "Atom Assist is doing work in the background, standby…"
    : isRouting
      ? "Finding the best workflow for your request"
      : isThinking
        ? PHRASES[phraseIndex]
        : isAutoStart
          ? "Starting up"
          : "Typing";

  return (
    <Box
      key="typing-indicator"
      id="chat-bubble-container-Assistant-typing"
      className="chat-bubble-container Assistant"
      sx={{ py: 2 }}
    >
      <Stack
        direction={lessThanMdBreakpoint ? "column" : "row"}
        gap={2}
        alignItems="flex-start"
      >
        <AssistantAvatar />
        <Box
          id="chat-bubble-content-Assistant-typing"
          className="chat-bubble-content"
          sx={{ width: "100%" }}
        >
          <AssistantTextContainer messageIndex={-1} chatMessage={undefined}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                py: 1,
                width: "100%",
              }}
            >
              <OrbitalSpinner />
              <Box
                sx={{
                  display: "flex",
                  alignItems: "baseline",
                  gap: 0.5,
                  minHeight: 24,
                }}
              >
                <Fade in={visible} timeout={300}>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {statusText}
                  </Typography>
                </Fade>
              </Box>
              {/* Elapsed timer pushed to the far right, matching the
                  sub-agent activity card's layout. */}
              {showTimer && (
                <Box sx={{ ml: "auto", flexShrink: 0 }}>
                  <ElapsedTimer />
                </Box>
              )}
            </Box>
          </AssistantTextContainer>
        </Box>
      </Stack>
    </Box>
  );
};

export default AtomAssistTypingIndicator;
